import { Select as SelectPrimitive } from '@base-ui/react/select'
import * as React from 'react'

import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { listMenuItemLabelClass, listMenuItemVariants } from '@/lib/component-constants'
import { cn } from '@/lib/utils'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, Info, XIcon } from 'lucide-react'
import SearchIcon from '@/assets/icons/search.svg'

type ItemEntry = { value: string; label: React.ReactNode }

const ItemRegistryContext = React.createContext<{
  register: (entry: ItemEntry) => void
  unregister: (value: string) => void
  getLabel: (value: unknown) => React.ReactNode | undefined
} | null>(null)

function Select<Value>({ children, ...props }: SelectPrimitive.Root.Props<Value>) {
  const itemsRef = React.useRef<ItemEntry[]>([])
  const [, forceUpdate] = React.useState(0)

  const registry = React.useMemo(
    () => ({
      register: (entry: ItemEntry) => {
        const idx = itemsRef.current.findIndex((i) => i.value === entry.value)
        if (idx === -1) {
          itemsRef.current.push(entry)
        } else {
          itemsRef.current[idx] = entry
        }
        forceUpdate((n) => n + 1)
      },
      unregister: (value: string) => {
        itemsRef.current = itemsRef.current.filter((i) => i.value !== value)
      },
      getLabel: (value: unknown) => {
        const match = itemsRef.current.find((i) => i.value === String(value))
        return match?.label
      },
    }),
    [],
  )

  return (
    <ItemRegistryContext.Provider value={registry}>
      <SelectPrimitive.Root {...props}>{children}</SelectPrimitive.Root>
    </ItemRegistryContext.Provider>
  )
}

function SelectGroup({ className, ...props }: SelectPrimitive.Group.Props) {
  return <SelectPrimitive.Group data-slot="select-group" className={cn('scroll-my-1 p-1', className)} {...props} />
}

function SelectValue({ className, placeholder, ...props }: SelectPrimitive.Value.Props) {
  const registry = React.useContext(ItemRegistryContext)

  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className={cn('flex flex-1 text-left', className)}
      placeholder={placeholder}
      {...props}
    >
      {(value) => {
        if (value == null || value === '') return placeholder
        const label = registry?.getLabel(value)
        return label ?? String(value)
      }}
    </SelectPrimitive.Value>
  )
}

function SelectTrigger({
  className,
  size = 'md',
  showDivider = false,
  children,
  ...props
}: SelectPrimitive.Trigger.Props & {
  size?: 'md' | 'lg'
  showDivider?: boolean
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "flex w-full cursor-pointer items-center justify-between gap-1.5 rounded-300 border-0 bg-transparent px-3 data-[size=md]:text-comp-input-ip1 data-[size=md]:leading-comp-input-ip1 text-[#555F6D] whitespace-nowrap transition-colors outline-none select-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:text-[#CFD6DD] disabled:opacity-100 aria-invalid:ring-0 data-placeholder:text-[#9EA8B3] data-[size=md]:py-1 data-[size=md]:px-3 data-[size=lg]:py-2 data-[size=lg]:px-3 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        showDivider && 'gap-2',
        className,
      )}
      {...props}
    >
      <div className={cn('p-1 w-[calc(100%-16px)]', showDivider && 'min-w-0 flex-1 w-auto px-0')}>{children}</div>
      <span
        className={cn(
          'flex shrink-0 items-center',
          showDivider && 'self-stretch border-l border-main-border-lv02 pl-2',
        )}
      >
        <SelectPrimitive.Icon
          className={(state) => cn(state.open && 'rotate-180')}
          render={
            <ChevronDownIcon
              className={cn(
                'pointer-events-none size-4 text-[#9EA8B3]',
                showDivider && 'size-6 text-creation-placeholder-icon-filled',
              )}
            />
          }
        />
      </span>
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  side = 'bottom',
  sideOffset = 8,
  align = 'start',
  alignOffset = 0,
  alignItemWithTrigger = false,
  ...props
}: SelectPrimitive.Popup.Props &
  Pick<SelectPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset' | 'alignItemWithTrigger'>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        className="isolate z-50"
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          className={cn(
            'relative isolate z-50 max-h-(--available-height) min-w-(--anchor-width) origin-(--transform-origin) overflow-y-auto rounded-300 bg-white text-[#555F6D] shadow-lg ring-1 ring-[#DEE3E7] data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-open:duration-200 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-closed:duration-150',
            className,
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List className="p-2">{children}</SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({ className, ...props }: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      className={cn('px-1.5 py-1 text-xs text-muted-foreground', className)}
      {...props}
    />
  )
}

function SelectItem({ className, children, value, ...props }: SelectPrimitive.Item.Props) {
  const registry = React.useContext(ItemRegistryContext)
  const stringValue = String(value)

  React.useEffect(() => {
    registry?.register({ value: stringValue, label: children })
    return () => registry?.unregister(stringValue)
  }, [registry, stringValue, children])

  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      value={value}
      className={cn(
        listMenuItemVariants({ interaction: 'select' }),
        "relative pr-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className={cn(listMenuItemLabelClass, 'flex items-center gap-2')}>
        {children}
      </SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({ className, ...props }: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('pointer-events-none -mx-1 my-1 h-px bg-[#DEE3E7]', className)}
      {...props}
    />
  )
}

function SelectScrollUpButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn(
        "top-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <ChevronUpIcon />
    </SelectPrimitive.ScrollUpArrow>
  )
}

function SelectScrollDownButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn(
        "bottom-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <ChevronDownIcon />
    </SelectPrimitive.ScrollDownArrow>
  )
}

interface SelectOption {
  value: string
  label: React.ReactNode
  disabled?: boolean
}

interface SimpleSelectProps {
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  items: SelectOption[]
  className?: string
  disabled?: boolean
  prependIcon?: React.ReactNode
  required?: boolean
  size?: 'md' | 'lg'
  hint?: string
  'aria-invalid'?: boolean
  'aria-label'?: string
  success?: boolean
  floatingLabel?: boolean | string
  searchable?: boolean
  searchPlaceholder?: string
  showDivider?: boolean
}

function SimpleSelect({
  value,
  onValueChange,
  placeholder,
  items,
  className,
  disabled,
  prependIcon,
  required,
  size = 'md',
  hint,
  'aria-invalid': ariaInvalid,
  'aria-label': ariaLabel,
  success,
  floatingLabel = true,
  searchable,
  searchPlaceholder = 'Search',
  showDivider = false,
}: SimpleSelectProps) {
  const [search, setSearch] = React.useState('')
  const displayLabel = items.find((i) => i.value === value)?.label ?? placeholder
  const floatingLabelText = typeof floatingLabel === 'string' ? floatingLabel : undefined
  const hasPersistentFloatingLabel = !!floatingLabelText?.trim()
  const normalizedSearch = search.trim().toLowerCase()

  const trigger = (
    <Select
      value={value ?? ''}
      onValueChange={onValueChange ? (v) => onValueChange(v ?? '') : undefined}
      disabled={disabled}
    >
      <SelectTrigger
        size={size}
        showDivider={showDivider}
        aria-invalid={ariaInvalid}
        aria-label={ariaLabel}
        data-success={success ? 'true' : undefined}
        className={cn(
          'border border-creation-neutral-container-on-surface-default bg-creation-secondary-container-surface-default',
          'hover:border-creation-primary-container-on-surface-hover',
          'active:border-creation-primary-container-on-surface-pressed',
          'data-open:border-creation-primary-container-on-surface-focused data-open:shadow-[0_0_4px_var(--color-primary-a30)]',
          'disabled:border-creation-neutral-container-on-surface-disable disabled:bg-creation-secondary-container-surface-disable',
          'aria-invalid:border-creation-error-container-on-surface-default',
          'data-[success=true]:border-creation-success-container-on-surface-default',
          className,
        )}
      >
        <div className="flex w-full min-w-0 flex-1 items-stretch">
          {prependIcon}
          <div className="min-w-0 flex-1 flex justify-between max-w-full">
            <span
              className={cn(
                'px-1 text-left text-comp-input-ip1 leading-comp-input-ip1 truncate',
                disabled
                  ? !value
                    ? 'text-creation-placeholder-text-disable'
                    : 'text-creation-placeholder-text-disable-filled'
                  : !value
                    ? 'text-creation-placeholder-text-default'
                    : 'text-creation-placeholder-text-filled',
              )}
              title={typeof displayLabel === 'string' ? displayLabel : undefined}
            >
              {displayLabel}
              {!value && required && !disabled && !hasPersistentFloatingLabel && (
                <span className="text-main-text-error">*</span>
              )}
            </span>
          </div>
        </div>
      </SelectTrigger>
      <SelectContent className="border-0 ring-0">
        {searchable && (
          <Input
            size="md"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            leadingIcon={<SearchIcon size={16} />}
            onKeyDown={(event) => event.stopPropagation()}
            floatingLabel={false}
          />
        )}
        {items.map((item) => (
          <SelectItem
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            className={cn(normalizedSearch && !String(item.label).toLowerCase().includes(normalizedSearch) && 'hidden')}
          >
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const labelText = value || hasPersistentFloatingLabel ? (floatingLabelText ?? placeholder) : undefined

  const coreEl =
    (placeholder || hasPersistentFloatingLabel) && floatingLabel ? (
      <div className="relative rounded-2xl overflow-visible mt-3">
        {labelText && (
          <div
            className={cn(
              'h-6 absolute left-3 top-0 flex gap-0.5 -translate-y-1/2 px-1 font-semibold z-10 text-ui-label-l1 leading-ui-label-l1',
              'flex items-end',
              'after:absolute after:inset-0 after:-z-10 after:top-1/2',
              disabled
                ? 'after:bg-creation-secondary-container-surface-disable'
                : 'after:bg-creation-secondary-container-surface-default',
            )}
          >
            <span className={disabled ? 'text-main-text-lv03' : 'text-main-text-lv01'}>{labelText}</span>
            {required && <span className="text-main-text-error">*</span>}
          </div>
        )}
        {trigger}
      </div>
    ) : (
      <div className="mt-3">{trigger}</div>
    )

  if (!hint) return coreEl

  return (
    <div className="flex flex-col gap-1">
      {coreEl}
      <div className="flex items-center gap-1">
        <Info size={12} className="text-main-icon-lv02" />
        <span className="text-main-icon-lv02 text-ui-caption-c1 leading-ui-caption-c1">{hint}</span>
      </div>
    </div>
  )
}

interface MultiSelectProps {
  options: SelectOption[]
  value: string[]
  onChange: (value: string[]) => void
  displayValue?: string
  placeholder?: string
  className?: string
  disabled?: boolean
  required?: boolean
  hint?: string
  showTags?: boolean
  showCheckbox?: boolean
  maxVisibleValues?: number
  size?: 'md' | 'lg'
  floatingLabel?: boolean
  onOpenChange?: (open: boolean) => void
}

function MultiSelect({
  options,
  value,
  onChange,
  displayValue,
  placeholder = 'Select...',
  className,
  disabled,
  required,
  hint,
  showTags = false,
  showCheckbox = false,
  maxVisibleValues,
  size = 'md',
  floatingLabel = true,
  onOpenChange,
}: MultiSelectProps) {
  const toggle = (optionValue: string) => {
    onChange(value.includes(optionValue) ? value.filter((v) => v !== optionValue) : [...value, optionValue])
  }

  const remove = (optionValue: string) => {
    onChange(value.filter((v) => v !== optionValue))
  }

  const selectedLabels = options.filter((o) => value.includes(o.value))
  const visibleLabels =
    maxVisibleValues === undefined ? selectedLabels : selectedLabels.slice(0, Math.max(0, Math.floor(maxVisibleValues)))
  const hiddenCount = selectedLabels.length - visibleLabels.length
  const hasSelection = selectedLabels.length > 0

  const floatingLabelText = floatingLabel && selectedLabels.length > 0 ? placeholder : undefined

  const trigger = (
    <Popover onOpenChange={onOpenChange}>
      <div className="relative rounded-300 overflow-visible">
        {floatingLabelText && (
          <div
            className={cn(
              'absolute left-3 top-0 flex gap-0.5 -translate-y-1/2 px-1 text-ui-label-l1 leading-ui-label-l1 font-semibold z-10',
              'after:absolute after:inset-0 after:-z-10 after:top-1/2',
              disabled
                ? 'after:bg-creation-secondary-container-surface-disable'
                : 'after:bg-creation-secondary-container-surface-default',
            )}
          >
            <span className={disabled ? 'text-main-text-lv03' : 'text-main-text-lv01'}>{floatingLabelText}</span>
            {required && !disabled && <span className="text-main-text-error">*</span>}
          </div>
        )}
        <PopoverTrigger
          disabled={disabled}
          data-size={size}
          className={cn(
            'flex w-full min-w-0 cursor-pointer items-center justify-between gap-1.5 rounded-300 border border-creation-neutral-container-on-surface-default bg-creation-secondary-container-surface-default px-3 text-comp-input-ip1 leading-comp-input-ip1 whitespace-nowrap outline-none select-none transition-colors',
            'data-[size=md]:py-1 data-[size=md]:px-3 data-[size=lg]:py-2 data-[size=lg]:px-3',
            '[&[data-popup-open]>svg]:rotate-180',
            'hover:border-creation-primary-container-on-surface-hover',
            'active:border-creation-primary-container-on-surface-pressed',
            'data-[state=open]:border-creation-primary-container-on-surface-focused data-[state=open]:shadow-[0_0_4px_var(--color-primary-a30)]',
            'disabled:cursor-not-allowed disabled:border-creation-neutral-container-on-surface-disable disabled:bg-creation-secondary-container-surface-disable disabled:text-[#CFD6DD]',
            className,
          )}
        >
          <span
            className={cn(
              'flex min-w-0 flex-1 items-center gap-1 text-left p-1',
              !hasSelection ? 'text-creation-placeholder-text-default' : 'text-creation-placeholder-text-filled',
            )}
          >
            <span className="min-w-0 truncate">
              {displayValue ??
                (hasSelection
                  ? visibleLabels.map((option, index) => (
                      <React.Fragment key={option.value}>
                        {index > 0 && ', '}
                        {option.label}
                      </React.Fragment>
                    ))
                  : placeholder)}
              {value.length === 0 && required && !disabled && '*'}
            </span>
            {displayValue === undefined && hiddenCount > 0 && <span className="shrink-0">+{hiddenCount}</span>}
          </span>
          <ChevronDownIcon className="pointer-events-none size-4 shrink-0 text-[#9EA8B3]" />
        </PopoverTrigger>
      </div>
      <PopoverContent align="start" sideOffset={4} className="w-(--anchor-width) rounded-300 border-0 p-2 gap-0 ring-0">
        {options.map((option) => {
          const checked = value.includes(option.value)
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => toggle(option.value)}
              className={cn(
                'relative flex w-full cursor-pointer items-center gap-2 rounded-300 py-1.5 pl-3 text-ui-body-b1 leading-ui-body-b1 text-creation-secondary-container-on-surface-default select-none outline-hidden',
                showCheckbox ? 'pr-3' : 'pr-8',
                'hover:bg-creation-primary-container-surface-hover hover:text-creation-primary-container-on-surface-hover',
                'active:bg-creation-primary-container-surface-pressed active:text-creation-primary-container-on-surface-pressed',
                checked &&
                  'bg-creation-primary-container-surface-focused text-creation-primary-container-on-surface-focused',
              )}
            >
              {showCheckbox && <Checkbox checked={checked} onCheckedChange={() => toggle(option.value)} />}
              <span className="min-w-0 flex-1 truncate text-left text-ui-body-b1 leading-ui-body-b1">
                {option.label}
              </span>
              {!showCheckbox && checked && (
                <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center text-[#097C69]">
                  <CheckIcon className="pointer-events-none size-4" />
                </span>
              )}
            </button>
          )
        })}
      </PopoverContent>
    </Popover>
  )

  const tagsEl = showTags && selectedLabels.length > 0 && (
    <div className="flex flex-wrap items-center gap-2">
      {selectedLabels.map((item) => (
        <span
          key={item.value}
          className="inline-flex items-center gap-1 rounded-full bg-[#E2F8F5] px-2.5 py-1 text-[12px] leading-4 text-[#097C69]"
        >
          {item.label}
          <button
            type="button"
            onClick={() => remove(item.value)}
            className="inline-flex size-3.5 items-center justify-center rounded-full hover:bg-[#097C69]/10"
          >
            <XIcon className="size-3" />
          </button>
        </span>
      ))}
    </div>
  )

  const coreEl = (
    <div className="flex flex-col gap-2 mt-2.5">
      {trigger}
      {tagsEl}
    </div>
  )

  if (!hint) return coreEl

  return (
    <div className="flex flex-col gap-1">
      {coreEl}
      <div className="flex items-center gap-1">
        <Info size={12} className="text-main-icon-lv02" />
        <span className="text-main-icon-lv02 text-ui-caption-c1 leading-ui-caption-c1">{hint}</span>
      </div>
    </div>
  )
}

export {
  MultiSelect,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  SimpleSelect,
}

export type { SelectOption }
